import { Box, Button, FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, Switch, TextField, Typography, Tooltip, IconButton } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import { useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PhoneInput from 'react-phone-number-input';
import { StepperContext, StepperContextModel } from "../../../contexts/request-stepper-context";
import { Country, State, City } from 'country-state-city';
import { useParams } from "react-router-dom";
import jobApplicationService from "../../../services/job-application-service";
import { educationalLevels, englishLevels, genderList, maritalStatusList, visaTypes, professions } from "../../../models/shared-data";

export interface PersonalInfo {
    identification: string;
    name: string;
    middleName: string;
    lastName: string;
    gender: number;
    maritalStatus: number;
    partnerName: string;
    haveKids: boolean;
    howManyKids: number;
    educationLevel: number,
    profession: string;
    speaksEnglish: boolean;
    englishLevel: number;
    workExperience: string;
    havePassport: boolean;
    passportExpiration: Date;
    visa: string;
    country: string;
    state: string;
    city: string;
    address: string;
    phone: string;
    birthDate: Date;
    haveEmail?: boolean;
    email?: string;
    jobOfferId?: number;
}

export default function PersonalInfoForm() {
    const { t } = useTranslation();
    const { handleNext, setPersonalInfo, personalInfo } = useContext(StepperContext) as StepperContextModel;
    const { register, handleSubmit, formState: { errors } } = useForm<PersonalInfo>();
    const [maritalStatus, setMaritalStatus] = useState<number>(0);
    const [haveKids, setHaveKids] = useState<boolean>(false);
    const [havePassport, setHavePassport] = useState<boolean>(false);
    const [haveEmail, setHaveEmail] = useState<boolean>(false);
    const [speaksEnglish, setSpeaksEnglish] = useState<boolean>(false);
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [selectedState, setSelectedState] = useState<string>('');
    let { jobOfferId } = useParams();

    const countries = Country.getAllCountries();
    const [states, setStates] = useState<Array<any>>([]);
    const [cities, setCities] = useState<Array<any>>([]);

    const [isIdValid, setIsIdValid] = useState<boolean>(true);

    useEffect(() => {
        setStates(State.getStatesOfCountry(selectedCountry));
    }, [selectedCountry]);

    useEffect(() => {
        setCities(City.getCitiesOfState(selectedCountry, selectedState));
    }, [selectedState]);

    const validateIdentification = (identification: string) => {
        jobApplicationService.validateApplicantId(identification)
            .then(response => {
                setIsIdValid(response.data);
            })
            .catch(error => {
                console.log('error', error);
            });
    }

    const handleForm: SubmitHandler<PersonalInfo> = (data) => {
        // Convert string values to proper types
        const formattedData = {
            ...data,
            jobOfferId: parseInt(jobOfferId ?? "0"),
            gender: typeof data.gender === 'string' ? parseInt(data.gender) : data.gender,
            maritalStatus: typeof data.maritalStatus === 'string' ? parseInt(data.maritalStatus) : data.maritalStatus,
            haveKids: Boolean(data.haveKids),
            howManyKids: data.howManyKids ? (typeof data.howManyKids === 'string' ? parseInt(data.howManyKids) : data.howManyKids) : 0,
            educationLevel: typeof data.educationLevel === 'string' ? parseInt(data.educationLevel) : data.educationLevel,
            speaksEnglish: Boolean(data.speaksEnglish),
            englishLevel: data.speaksEnglish ? (typeof data.englishLevel === 'string' ? parseInt(data.englishLevel) : data.englishLevel) : 0,
            havePassport: Boolean(data.havePassport),
            // Only include email if haveEmail is true
            email: data.haveEmail ? data.email : undefined
        };
        
        // Remove haveEmail from the final data as it's not expected by API
        delete (formattedData as any).haveEmail;
        
        setPersonalInfo(formattedData);
        if (isIdValid) {
            handleNext();
        }
    }

    return (
        <Box sx={{ padding: 2 }}>
            <form onSubmit={handleSubmit(handleForm)} style={{ marginTop: 20, marginBottom: 10 }}>
                <TextField
                    id="id"
                    label={t('forms.personalInfo.id')}
                    variant="outlined"
                    size="small"
                    defaultValue={personalInfo?.identification}
                    error={errors!.identification ? true : false}
                    {...register('identification', {
                        required: 'Id is required',
                        pattern: { value: /^[0-9]+$/, message: 'The Id must be number.' },
                        maxLength: { value: 13, message: 'Must have 13 characters' },
                        minLength: { value: 13, message: 'Must have 13 characters' }
                    })}
                    helperText={errors!.identification?.message!}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}
                    onBlur={(e) => validateIdentification(e.target.value)}
                />
                <Typography variant="caption" color="error" sx={{
                    display: isIdValid ? 'none' : 'block',
                    width: { xs: '100%', sm: '48%' }
                }}>
                    {t('forms.personalInfo.idIsNotValid')}
                </Typography>

                <TextField
                    id="name"
                    label={t('forms.personalInfo.name')}
                    variant="outlined"
                    size="small"
                    defaultValue={personalInfo?.name}
                    {...register('name', { required: true })}
                    error={errors!.name ? true : false}
                    helperText={errors.name?.message!}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}
                />

                <TextField
                    id="middleName"
                    label={t('forms.personalInfo.middleName')}
                    variant="outlined"
                    size="small"
                    defaultValue={personalInfo?.middleName}
                    {...register('middleName')}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}
                />

                <TextField
                    id="lastName"
                    label={t('forms.personalInfo.lastName')}
                    variant="outlined"
                    size="small"
                    defaultValue={personalInfo?.lastName}
                    {...register('lastName', { required: 'Field is required' })}
                    helperText={errors.lastName?.message}
                    error={errors!.lastName ? true : false}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}
                />

                <TextField
                    id="birthDate"
                    label={t('forms.personalInfo.birthDate')}
                    defaultValue={personalInfo?.birthDate}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    type="date"
                    size="small"
                    {...register('birthDate', { required: true })}
                    error={errors!.birthDate ? true : false}
                    helperText={errors.birthDate?.message}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}
                />

                <TextField
                    id="gender"
                    label={t('forms.personalInfo.gender')}
                    defaultValue={personalInfo?.gender}
                    variant="outlined"
                    size="small"
                    select
                    {...register('gender', { required: 'Select an option' })}
                    error={errors!.gender ? true : false}
                    helperText={errors.gender?.message}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}>
                    {genderList.map(gender => (
                        <MenuItem key={gender.value} value={gender.value}>
                            {t(gender.label)}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="maritalStatus"
                    label={t('forms.personalInfo.maritalStatus')}
                    defaultValue={personalInfo?.maritalStatus}
                    variant="outlined"
                    size="small"
                    select
                    {...register('maritalStatus', {
                        required: 'Select an option',
                        onChange: (event) => setMaritalStatus(event.target.value)
                    })}
                    error={errors!.maritalStatus ? true : false}
                    helperText={errors.maritalStatus?.message}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}>
                    {maritalStatusList.map(maritalStatus => (
                        <MenuItem key={maritalStatus.value} value={maritalStatus.value}>
                            {t(maritalStatus.label)}
                        </MenuItem>
                    ))}
                </TextField>

                {maritalStatus == 2 && (
                    <TextField
                        id="partner"
                        label={t('forms.personalInfo.partnerName')}
                        defaultValue={personalInfo?.partnerName}
                        variant="outlined"
                        size="small"
                        {...register('partnerName')}
                        sx={{
                            width: { xs: '100%', sm: '48%' },
                            marginTop: 2,
                            marginRight: { sm: 2, xs: 0 }
                        }}
                    />
                )}

                <FormControl component="fieldset" sx={{
                    marginTop: 2,
                    marginRight: { sm: 2, xs: 0 },
                    width: { xs: '100%', sm: '48%' }
                }}>
                    <FormLabel component="legend">{t('forms.personalInfo.haveKids')}</FormLabel>
                    <RadioGroup row {...register('haveKids', {
                        onChange: (event) => setHaveKids(event.target.value === 'true')
                    })} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')}/>
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                {haveKids && (
                    <TextField
                        id="howManyKids"
                        label={t('forms.personalInfo.howManyKids')}
                        variant="outlined"
                        size="small"
                        type="number"
                        defaultValue={personalInfo?.howManyKids}
                        {...register('howManyKids', { 
                            required: haveKids ? t('forms.personalInfo.howManyKidsRequired') : false,
                            min: { value: 1, message: t('forms.personalInfo.howManyKidsMin') },
                            pattern: { value: /^[0-9]+$/, message: t('forms.personalInfo.howManyKidsPattern') }
                        })}
                        error={errors!.howManyKids ? true : false}
                        helperText={errors.howManyKids?.message}
                        sx={{
                            width: { xs: '100%', sm: '48%' },
                            marginTop: 2,
                            marginRight: { sm: 2, xs: 0 }
                        }}
                    />
                )}

                <TextField
                    id="educationLevel"
                    label={t('forms.personalInfo.educationLevel')}
                    variant="outlined"
                    size="small"
                    defaultValue={personalInfo?.educationLevel}
                    select
                    {...register('educationLevel', { required: true })}
                    error={errors!.educationLevel ? true : false}
                    helperText={errors.educationLevel?.message}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}>
                    {educationalLevels.map(level => (
                        <MenuItem key={level.value} value={level.value}>
                            {t(level.label)}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="profession"
                    label={t('forms.personalInfo.profession')}
                    defaultValue={personalInfo?.profession}
                    variant="outlined"
                    size="small"
                    select
                    {...register('profession', { required: true })}
                    error={errors!.profession ? true : false}
                    helperText={errors.profession?.message}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}>
                    {professions.map(profession => (
                        <MenuItem key={profession.value} value={profession.value}>
                            {t(profession.label)}
                        </MenuItem>
                    ))}
                </TextField>

                <FormControl component="fieldset" sx={{
                    marginTop: 2,
                    marginRight: { sm: 2, xs: 0 },
                    width: { xs: '100%', sm: '48%' }
                }}>
                    <FormLabel component="legend">{t('forms.personalInfo.speaksEnglish')}</FormLabel>
                    <RadioGroup row {...register('speaksEnglish', {
                        onChange: (event) => setSpeaksEnglish(event.target.value === 'true')
                    })} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')}/>
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                {speaksEnglish && (
                    <TextField
                        id="englishLevel"
                        label={t('forms.personalInfo.englishLevel')}
                        variant="outlined"
                        size="small"
                        defaultValue={personalInfo?.englishLevel}
                        select
                        {...register('englishLevel', { required: speaksEnglish ? 'Select an option' : false })}
                        error={errors!.englishLevel ? true : false}
                        helperText={errors.englishLevel?.message}
                        sx={{
                            width: { xs: '100%', sm: '48%' },
                            marginTop: 2,
                            marginRight: { sm: 2, xs: 0 }
                        }}>
                        {englishLevels.filter(level => level.value <= 3).map(level => (
                            <MenuItem key={level.value} value={level.value}>
                                {t(level.label)}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: 2 }}>
                    <TextField
                        id="workExperience"
                        label={t('forms.personalInfo.workExperience')}
                        defaultValue={personalInfo?.workExperience}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                        {...register('workExperience', { 
                            required: true,
                            maxLength: { value: 200, message: t('forms.personalInfo.workExperienceMaxLength') }
                        })}
                        error={errors!.workExperience ? true : false}
                        helperText={errors.workExperience?.message}
                        sx={{ width: '100%' }}
                    />
                    <Tooltip title={t('forms.personalInfo.workExperienceTooltip')} arrow>
                        <IconButton size="small" sx={{ ml: 1 }}>
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <FormControl component="fieldset" sx={{
                    marginTop: 2,
                    marginRight: { sm: 2, xs: 0 },
                    width: { xs: '100%', sm: '48%' }
                }}>
                    <FormLabel component="legend">{t('forms.personalInfo.havePassport')}</FormLabel>
                    <RadioGroup row {...register('havePassport', {
                        onChange: (event) => setHavePassport(event.target.value === 'true')
                    })} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')}/>
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                {havePassport && (
                    <TextField
                        id="passportExpiration"
                        label={t('forms.personalInfo.passportExpiration')}
                        defaultValue={personalInfo?.passportExpiration}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        type="date"
                        size="small"
                        {...register('passportExpiration', { 
                            required: havePassport ? t('forms.personalInfo.passportExpirationRequired') : false
                        })}
                        error={errors!.passportExpiration ? true : false}
                        helperText={errors.passportExpiration?.message}
                        sx={{
                            width: { xs: '100%', sm: '48%' },
                            marginTop: 2,
                            marginRight: { sm: 2, xs: 0 }
                        }}
                    />
                )}

                <TextField
                    id="visa"
                    label={t('forms.personalInfo.visa')}
                    variant="outlined"
                    size="small"
                    defaultValue={personalInfo?.visa}
                    select
                    {...register('visa')}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}>
                    {visaTypes.map(visa => (
                        <MenuItem key={visa.value} value={t(visa.label)}>
                            {t(visa.label)}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="country"
                    label={t('forms.personalInfo.country')}
                    variant="outlined"
                    size="small"
                    select
                    {...register('country', {
                        required: 'Select a country',
                        onChange: (event) => setSelectedCountry(event.target.value)
                    })}
                    defaultValue={personalInfo?.country}
                    error={errors!.country ? true : false}
                    helperText={errors.country?.message}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}>
                    {countries.map((country, index) => (
                        <MenuItem key={country.isoCode + index} value={country.isoCode}>
                            {country.name}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="state"
                    label={t('forms.personalInfo.state')}
                    variant="outlined"
                    size="small"
                    select
                    {...register('state', {
                        required: 'Select a state',
                        onChange: (event) => setSelectedState(event.target.value)
                    })}
                    defaultValue={personalInfo?.state}
                    error={errors!.state ? true : false}
                    helperText={errors.state?.message}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}>
                    {states.map((state, index) => (
                        <MenuItem key={state.isoCode + index} value={state.isoCode}>
                            {state.name}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="city"
                    label={t('forms.personalInfo.city')}
                    variant="outlined"
                    size="small"
                    select
                    {...register('city', { required: true })}
                    defaultValue={personalInfo?.city}
                    error={errors!.city ? true : false}
                    helperText={errors.city?.message}
                    sx={{
                        width: { xs: '100%', sm: '48%' },
                        marginTop: 2,
                        marginRight: { sm: 2, xs: 0 }
                    }}>
                    {cities.map((city, index) => (
                        <MenuItem key={city.name + index} value={city.name}>
                            {city.name}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="address"
                    label={t('forms.personalInfo.address')}
                    defaultValue={personalInfo?.address}
                    variant="outlined"
                    size="small"
                    {...register('address', { required: true })}
                    error={errors!.address ? true : false}
                    helperText={errors.address?.message}
                    sx={{
                        width: '100%',
                        marginTop: 2
                    }}
                />

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: 2
                }}>
                    <Typography variant="caption">{t('forms.personalInfo.phone')}</Typography>
                    <PhoneInput
                        id="phone"
                        international
                        countryCallingCodeEditable={false}
                        {...register('phone', { required: true })}
                        defaultValue={personalInfo?.phone}
                        defaultCountry={selectedCountry as any}
                        onChange={(value) => register('phone').onChange({ target: { value } })}
                        style={{
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            borderRadius: 4,
                            padding: 10,
                            fontSize: 16,
                            marginBottom: errors.phone ? 10 : 0,
                        }}
                    />
                    {errors.phone && (
                        <Typography variant="caption" color="error">
                            {'Field required'}
                        </Typography>
                    )}
                </Box>

                <FormControl component="fieldset" sx={{
                    marginTop: 2,
                    marginRight: { sm: 2, xs: 0 },
                    width: { xs: '100%', sm: '48%' }
                }}>
                    <FormLabel component="legend">{t('forms.personalInfo.haveEmail')}</FormLabel>
                    <RadioGroup row {...register('haveEmail', {
                        onChange: (event) => setHaveEmail(event.target.value === 'true')
                    })} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')}/>
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                {haveEmail && (
                    <TextField
                        id="email"
                        label={t('forms.personalInfo.email')}
                        variant="outlined"
                        size="small"
                        defaultValue={personalInfo?.email}
                        {...register('email', { 
                            required: haveEmail ? t('forms.personalInfo.emailRequired') : false,
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t('forms.personalInfo.emailPattern')
                            }
                        })}
                        error={errors!.email ? true : false}
                        helperText={errors.email?.message}
                        sx={{
                            width: '100%',
                            marginTop: 2
                        }}
                    />
                )}

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 4
                }}>
                    <Button variant="contained" type="submit">
                        {t('forms.personalInfo.next')}
                    </Button>
                </Box>
            </form>
        </Box>
    )
}
